import {useTranslation} from "react-i18next";
import {useQuery} from "@tanstack/react-query";
import {Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import {Button} from "@/components/ui/button.tsx";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "@/components/ui/hover-card";
import ExpandableText from "@/components/ExpandableText.tsx";
import {LucideMail} from "lucide-react";
import {WhatsAppIcon} from "@/components/Icons.tsx";
import {adjustTimezone, convertTo12Hour, timezoneResolver} from "@/utils/time-helper.ts";
import {getImgSrc} from "@/utils/file-upload.ts";
import {activeLocale, getLocalizedFieldName, LocalizedField} from "@/utils/translation-helper.ts";
import {getMatches} from "@/utils/suggestion-helper.ts";
import {MentorWithoutAssociations} from "@/types/Mentor.ts";
import {MenteeWithPreferenceAndSuggestionsDetail} from "@/types/Mentee.ts";
import {Suggestion} from "@/types/Suggestion.ts";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getItemsOfAllTypes} from "@/services/itemService.ts";

interface SuggestedMentorCardProps {
    menteeWithSuggestedMentors: MenteeWithPreferenceAndSuggestionsDetail;
    suggestions: Suggestion[];
    available?: boolean;
    isAdmin?: boolean;
    handleSelect?: (id: number) => void;
    showAvailabilities?: boolean;
    startIndex?: number;
}

const SuggestedMentorCard = ({
                                 menteeWithSuggestedMentors,
                                 suggestions,
                                 available,
                                 isAdmin = false,
                                 handleSelect,
                                 showAvailabilities = true,
                                 startIndex = 0,
                             }: SuggestedMentorCardProps) => {
    const {t} = useTranslation();

    const {data: criteria} = useQuery({
        queryKey: queryKeys.fetchCriteria.all,
        queryFn: () => getItemsOfAllTypes(),
    });

    return (
        <ul>
            {suggestions.map((suggestion, idx: number) => {
                const {broadMatched, specificMatched} = getMatches(
                    criteria,
                    suggestion.brScore,
                    suggestion.spScore,
                    menteeWithSuggestedMentors?.menteePreference
                );

                const isSelected = menteeWithSuggestedMentors?.mentor?.id === suggestion.mentor.id;
                const mentorName =
                    suggestion.mentor?.user?.fullName ??
                    ([suggestion.mentor?.user?.firstName, suggestion.mentor?.user?.lastName].filter(Boolean).join(" ") || "");

                const hasAffinityGroups = Boolean(
                    menteeWithSuggestedMentors?.menteePreference?.affinityGroups &&
                    menteeWithSuggestedMentors.menteePreference.affinityGroups.length > 0
                );
                const hasBroad = broadMatched.length > 0;
                const hasSpecific = specificMatched.length > 0;

                return (
                    <li key={idx} className="mb-6 last:mb-0">
                        <div
                            className={`relative rounded-xl border bg-bg-card p-6 shadow-sm transition-colors sm:flex sm:items-start sm:gap-6 ${
                                isSelected 
                                    ? "border-2 border-accent bg-accent/5" 
                                    : "border-border hover:border-border/80"
                            }`}>
                            <div className="absolute right-4 top-4 rounded-full border border-transparent bg-text-1 px-3 py-1.5 text-sm font-semibold text-bg-card shadow-sm">
                                #{startIndex + idx + 1}
                            </div>
                            <div className="flex flex-col items-center sm:items-start flex-shrink-0 mb-4 sm:mb-0 w-full sm:w-28">
                                <Avatar className="h-28 w-28 rounded-xl border-2 border-border mb-3">
                                    <AvatarImage 
                                        src={getImgSrc(suggestion.mentor.user?.profilePictureSrc) ?? import.meta.env.VITE_DEFAULT_AVATAR}
                                        alt={mentorName}
                                        className="object-cover"
                                    />
                                    <AvatarFallback className="rounded-xl bg-accent/15 text-2xl font-semibold text-text-1">
                                        {mentorName.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                                {(available || isAdmin) && (
                                    <Button
                                        onClick={() => handleSelect ? handleSelect(suggestion.mentor.id) : null}
                                        className={`w-28 rounded-md px-4 py-2 h-10 font-semibold transition-colors ${
                                            isSelected
                                                ? "bg-bg-elevated text-text-2 border border-border cursor-not-allowed"
                                                : "bg-text-1 text-bg-card hover:opacity-95"
                                        }`}
                                        disabled={isSelected}
                                    >
                                        {isSelected ? t("common.selected") : t("common.select")}
                                    </Button>
                                )}
                            </div>
                            <div className="flex-grow min-w-0 w-full sm:text-left text-center">
                                <div className="mb-4">
                                    <HoverCard>
                                        <HoverCardTrigger asChild>
                                            <span className="inline-flex items-center gap-2">
                                                <a 
                                                    href={`/mentors/${suggestion.mentor.id}`}
                                                    className="text-xl font-semibold text-text-1 hover:text-springGreen transition-colors underline-offset-4 hover:underline"
                                                >
                                                    {mentorName}
                                                </a>
                                                {suggestion.mentor.isOnWhatsApp === true && (
                                                    <WhatsAppIcon 
                                                        className="h-5 w-5 shrink-0 text-[#25D366]" 
                                                        title={t("mentor.availableOnWhatsApp")}
                                                    />
                                                )}
                                            </span>
                                        </HoverCardTrigger>
                                        <HoverCardContent align={"start"} className="w-80 rounded-xl border border-border bg-bg-card">
                                            <div className="flex gap-4">
                                                <Avatar className="h-12 w-12 rounded-xl border border-border">
                                                    <AvatarImage
                                                        src={getImgSrc(suggestion.mentor.user?.profilePictureSrc)}
                                                        className="object-cover"
                                                    />
                                                    <AvatarFallback className="rounded-xl bg-accent/15 text-text-1">
                                                        {suggestion.mentor.user?.firstName?.charAt(0)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="space-y-1 flex-1 min-w-0">
                                                    <h4 className="font-semibold text-base text-text-1">{mentorName}</h4>
                                                    <div className="flex items-center gap-2">
                                                        <LucideMail className="h-4 w-4 text-text-3 flex-shrink-0"/>
                                                        <span className="text-xs text-text-2 truncate">
                                                            {suggestion.mentor.user?.email}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-text-2 pt-2">
                                                        {suggestion.mentor[getLocalizedFieldName("title") as keyof MentorWithoutAssociations]?.toString()}, {suggestion.mentor[getLocalizedFieldName("university") as keyof MentorWithoutAssociations]?.toString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </HoverCardContent>
                                    </HoverCard>
                                </div>
                                {showAvailabilities && (
                                    <div className="space-y-3 mb-4">
                                        {suggestion.mentor.timezone && (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm text-text-2">
                                                    {t("dashboard.availabilityIn")}
                                                </span>
                                                <span className="text-sm font-semibold text-text-1">
                                                    {timezoneResolver[suggestion.mentor.timezone][activeLocale() as keyof LocalizedField]}
                                                </span>
                                            </div>
                                        )}
                                        {suggestion.mentor.availabilities && suggestion.mentor.availabilities.length > 0 && (
                                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                                {suggestion.mentor.availabilities
                                                    .sort((a, b) => new Date(a.availableOn).getTime() - new Date(b.availableOn).getTime())
                                                    .map((a, idx) => (
                                                        <div key={idx} className="rounded-lg border border-border bg-bg-elevated/60 p-3">
                                                            <h3 className="text-xs font-semibold text-text-2 uppercase tracking-wide mb-2">
                                                                {t("mentor.session")} {idx + 1}
                                                            </h3>
                                                            <p className="text-sm font-medium text-text-1 mb-1">
                                                                {adjustTimezone(a.availableOn).toDateString()}
                                                            </p>
                                                            <p className="text-sm text-text-2">
                                                                {convertTo12Hour(a.availableFrom)} - {convertTo12Hour(a.availableTill)}
                                                            </p>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                        <p className="text-sm text-text-3">
                                            <a 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-brand-arctic hover:text-springGreen underline-offset-4 hover:underline transition-colors inline-flex items-center"
                                                href="https://nrc.canada.ca/en/certifications-evaluations-standards/canadas-official-time/time-zones-daylight-saving-time"
                                            >
                                                {t("dashboard.doubleCheckDiff")}
                                            </a>{" "}
                                            {t("dashboard.betweenTimezones")}
                                        </p>
                                    </div>
                                )}
                                {(hasAffinityGroups || hasBroad || hasSpecific) && (
                                    <div className="space-y-3 mb-4 pt-4 border-t border-border">
                                        {hasAffinityGroups && (
                                            <div className="space-y-2">
                                                <span className="text-sm font-semibold text-text-1 block">
                                                    {t("affinity.group")}:
                                                </span>
                                                <div className="flex flex-wrap gap-2">
                                                    {menteeWithSuggestedMentors.menteePreference.affinityGroups?.map((item, index) => {
                                                        const affinityGroupName = item.affinityGroup[getLocalizedFieldName("name") as keyof typeof item.affinityGroup] as string;
                                                        const mentorAffinityGroupId = suggestion.mentor.affinityGroupId;
                                                        const isMatch = mentorAffinityGroupId === item.affinityGroup.id;
                                                        return (
                                                            <Badge
                                                                key={index}
                                                                variant="outline"
                                                                className={`rounded-full px-3 py-1 text-sm font-normal border-0 transition-[filter,box-shadow] duration-150 ${
                                                                    isMatch
                                                                        ? "bg-status-success text-white hover:brightness-110 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.35)]"
                                                                        : "bg-status-danger text-white hover:brightness-110"
                                                                }`}
                                                            >
                                                                {affinityGroupName}
                                                            </Badge>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {hasBroad && (
                                            <div className="space-y-2">
                                                <span className="text-sm font-semibold text-text-1 block">
                                                    {t("criteria.broadCriteria")}:
                                                </span>
                                                <div className="flex flex-wrap gap-2">
                                                    {broadMatched.map((item, index) => (
                                                        <Badge 
                                                            key={index}
                                                            variant="outline"
                                                            className={`rounded-full px-3 py-1 text-sm font-normal border-0 transition-[filter,box-shadow] duration-150 ${
                                                                item.match 
                                                                    ? "bg-status-success text-white hover:brightness-110 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.35)]" 
                                                                    : "bg-status-danger text-white hover:brightness-110"
                                                            }`}
                                                        >
                                                            {t(`criteria.${item.key}`)} → {item.value}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {hasSpecific && (
                                            <div className="space-y-2">
                                                <span className="text-sm font-semibold text-text-1 block">
                                                    {t("criteria.specificCriteria")}:
                                                </span>
                                                <div className="flex flex-wrap gap-2">
                                                    {specificMatched.map((item, index) => (
                                                        <Badge 
                                                            key={index}
                                                            variant="outline"
                                                            className={`rounded-full px-3 py-1 text-sm font-normal border-0 transition-[filter,box-shadow] duration-150 ${
                                                                item.match 
                                                                    ? "bg-status-success text-white hover:brightness-110 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.35)]" 
                                                                    : "bg-status-danger text-white hover:brightness-110"
                                                            }`}
                                                        >
                                                            {item.keys.length === 1 ? (
                                                                <>
                                                                    {t(`criteria.${item.keys[0]}`)} → {item.values[0]}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {t(`criteria.${item.keys[0]}`)} → {item.values[0]} & {t(`criteria.${item.keys[1]}`)} → {item.values[1]}
                                                                </>
                                                            )}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {suggestion.mentor[getLocalizedFieldName("bio") as keyof MentorWithoutAssociations] && (
                                    <div className="pt-4 border-t border-border">
                                        <ExpandableText
                                            text={suggestion.mentor[getLocalizedFieldName("bio") as keyof MentorWithoutAssociations]?.toString() || null}
                                            maxLength={300}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

export default SuggestedMentorCard;
